import { useNavigate } from "react-router-dom"
import { motion } from "framer-motion"

function ConfettiDots() {
  const colors = ["#f472b6", "#a78bfa", "#34d399", "#fbbf24", "#60a5fa", "#f87171"]

  return (
    <div className="flex justify-center gap-1.5 mb-4">
      {colors.map((c, i) => (
        <motion.span
          key={i}
          className="w-2.5 h-2.5 rounded-full inline-block"
          style={{ backgroundColor: c }}
          animate={{
            y: [0, -10, 0],
          }}
          transition={{
            duration: 0.6,
            repeat: Infinity,
            delay: i * 0.8,
            ease: "easeInOut",
          }}
        />
      ))}
    </div>
  )
}

export default function Step3Result({ plan, billing, payment, result, onBackToSubscription }) {
  const navigate = useNavigate()
  const vat = Math.round((plan.price_monthly || 0) * 0.07)
  const total = (plan.price_monthly || 0) + vat

  // Generate a mock order number
  const orderNo = result?.order_no || `#KZ-${new Date().getFullYear() + 543}-${String(Math.floor(Math.random() * 99999)).padStart(6, "0")}`
  const today = new Date()
  const formatTH = (d) =>
    d.toLocaleDateString("th-TH", { day: "2-digit", month: "short", year: "numeric", calendar: "buddhist" }) +
    ", " + d.toLocaleTimeString("th-TH", { hour: "2-digit", minute: "2-digit" })
  const next = new Date(today)
  next.setMonth(next.getMonth() + 1)
  const formatDateTH = (d) =>
    d.toLocaleDateString("th-TH", { day: "2-digit", month: "short", year: "numeric", calendar: "buddhist" })

  if (result?.success === false) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center max-w-sm mx-auto">
        <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-5">
          <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </div>
        <h2 className="text-2xl font-bold mb-2">ชำระเงินไม่สำเร็จ ❌</h2>
        <p className="text-gray-500 text-sm mb-1">
          {result?.message || "เกิดข้อผิดพลาดในการชำระเงิน กรุณาลองใหม่อีกครั้ง"}
        </p>
        <p className="text-gray-400 text-xs mb-8">หากปัญหายังคงอยู่ กรุณาติดต่อทีมสนับสนุน</p>
        <div className="flex flex-col sm:flex-row w-full gap-3">
          <button
            onClick={onBackToSubscription}
            className="w-full sm:flex-1 px-5 py-2.5 rounded-xl border border-gray-300 text-sm font-medium hover:bg-gray-50 transition"
          >
            ← กลับหน้า Subscription
          </button>
          <button
            onClick={() => window.location.reload()}
            className="w-full sm:flex-1 px-5 py-2.5 rounded-xl bg-pink-500 hover:bg-pink-600 text-white text-sm font-medium transition"
          >
            ลองใหม่อีกครั้ง
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <ConfettiDots />

      {/* Check circle */}
      <div className="w-16 h-16 rounded-full border-4 border-green-400 flex items-center justify-center mb-5 bg-white shadow">
        <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
        </svg>
      </div>

      <h2 className="text-2xl font-bold mb-2">ชำระเงินสำเร็จแล้ว! 🎉</h2>
      <p className="text-gray-500 text-sm max-w-sm mb-1">
        ยินดีต้อนรับสู่แผน <strong>{plan.display_name}</strong>
      </p>
      <p className="text-gray-400 text-xs max-w-sm mb-8">
        คุณสามารถเริ่มใช้งานตัวเลือกที่มีทั้งหมดได้ทันที<br />
        ใบเสร็จจะถูกส่งไปยังอีเมลของคุณภายใน 5 นาที
      </p>

      {/* Receipt card */}
      <div className="bg-white border border-gray-200 rounded-2xl p-6 w-full max-w-sm text-left space-y-3.5 mb-8 shadow-sm">
        {[
          { label: "หมายเลขคำสั่งซื้อ", value: <span className="font-bold">{orderNo}</span> },
          { label: "แผนที่เลือก", value: plan.display_name },
          { label: "ยอดชำระ", value: <span className="text-pink-500 font-bold">฿{total.toLocaleString()}.00</span> },
          { label: "วันที่ชำระ", value: formatTH(today) },
          { label: "ก่อล่อายุถึง", value: formatDateTH(next) },
          {
            label: "ใบเสร็จ",
            value: (
              <button
                className="text-pink-500 underline hover:text-pink-600 text-sm"
                onClick={() => alert("ดาวน์โหลด PDF (mock)")}
              >
                ดาวน์โหลด PDF
              </button>
            )
          },
        ].map(({ label, value }) => (
          <div key={label} className="flex justify-between items-center text-sm">
            <span className="text-gray-500">{label}</span>
            <span className="text-right">{value}</span>
          </div>
        ))}
      </div>

      <div className="flex flex-col sm:flex-row w-full max-w-sm gap-3">
        <button
          onClick={onBackToSubscription}
          className="w-full sm:flex-1 justify-center flex items-center gap-1 px-5 py-2.5 rounded-xl border border-gray-300 text-sm font-medium hover:bg-gray-50 transition"
        >
          ← กลับหน้า Subscription
        </button>
        <button
          onClick={() => navigate("/dashboard")}
          className="w-full sm:flex-1 justify-center px-5 py-2.5 rounded-xl bg-pink-500 hover:bg-pink-600 text-white text-sm font-semibold transition"
        >
          เริ่มใช้งานเลย →
        </button>
      </div>
    </div>
  )
}
